// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from '@shared/commons/Geometry';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { Pen, ShadowBrush, SolidBrush } from '@shared/commons/Graphics';
import { ClientType } from '@shared/utils/ClientType';
export class PlateBase {
    constructor() {
        this.clientRectangle = new Rectangle();
        this.closeRectangle = new Rectangle();
        this.editRectangle = new Rectangle();
        this.ellipseRectangle = new Rectangle();
        this.visibilityRectangle = new Rectangle();
        this.lastWidth = 0;
        this.lastParametersWidth = 0;
        this.width = 0;
        this.lastBtnsX = 0;
        this.lastClientRectangleX = 0;
        this.PlateVisible = true;
        this.lastLinesCount = 0;
        this.tooltip = null;
        this.lastHover = PlateHoverEnum.None;
        this.nullBarIndex = -1;
        this.gap = 4;
        this.paddingX = 8;
        this.paddingY = 0;
        this.circleDiameter = 12;
        this.btnSize = 20;
        PlateBase.ThemeChanged();
        ThemeManager.onThemeChange.Subscribe(PlateBase.ThemeChanged);
    }
    GetHover() {
        return false;
    }
    SetHover(value) { }
    GetSelected() { }
    SetSelected() { }
    GetVisible() {
        return false;
    }
    get Width() {
        const w = Math.max(this.lastWidth, 80); // min is 80 px
        // w = Math.min(w, 300); // max is 300
        if (this.width < w) {
            this.width = w;
        }
        // запоминаем макс размер
        return this.width;
    }
    calculatePlateSizes(gr, window, cashItemSeries, lastMouseMovePt) {
        const clientRectangle = this.clientRectangle;
        if (clientRectangle.IsEmpty())
            return;
        let curX = clientRectangle.X + this.paddingX;
        const curY0 = clientRectangle.Y;
        const curY = curY0;
        const plateH = clientRectangle.Height - this.paddingY;
        if (this.isOverlay) {
            this.ellipseRectangle = new Rectangle(curX, curY + this.paddingY + (plateH - this.circleDiameter) / 2, this.circleDiameter, this.circleDiameter);
            curX += this.ellipseRectangle.Width + this.gap;
        }
        else {
            this.ellipseRectangle = Rectangle.Empty();
        }
        this.editRectangle = new Rectangle(curX, curY, 0, plateH);
        // name
        const name = this.getTextName();
        const nameWidth = gr.GetTextWidth(name, PlateBase.font);
        this.nameRect = new Rectangle(curX, curY + this.paddingY, nameWidth, plateH);
        curX += this.nameRect.Width + this.gap;
        const lastMouseHoverBarIndex = this.getMouseHoverBarIndex(lastMouseMovePt, window, cashItemSeries);
        // all lines
        const newLinesCount = this.getLinesCount();
        this.platesBack = [];
        for (let i = 0; i < newLinesCount; i++) {
            const text = this.getTextValue(lastMouseHoverBarIndex, i, cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.settings);
            if (!text) {
                continue;
            }
            const padding = 4;
            const priceText = gr.GetTextWidth(text, PlateBase.font) + padding * 2;
            const plateBackRect = new Rectangle(curX, curY + this.paddingY, priceText, plateH);
            curX += plateBackRect.Width;
            this.platesBack.push({ rect: plateBackRect, text, index: i });
        }
        if (curX > this.lastBtnsX || this.lastClientRectangleX !== clientRectangle.X) {
            this.lastBtnsX = curX;
        }
        this.lastClientRectangleX = clientRectangle.X;
        if (clientRectangle.Width > 30) {
            curX = this.lastBtnsX;
            this.editRectangle.Width = curX - this.editRectangle.X;
            this.visibilityRectangle = new Rectangle(curX, curY, this.btnSize, plateH);
            curX += this.visibilityRectangle.Width;
            this.closeRectangle = new Rectangle(curX, curY, this.btnSize, plateH);
            curX += this.closeRectangle.Width;
        }
        else {
            this.closeRectangle = Rectangle.Empty();
            this.visibilityRectangle = Rectangle.Empty();
        }
        const newRealWidth = Math.round(curX - clientRectangle.X);
        this.plateRect = new Rectangle(clientRectangle.X, curY0, newRealWidth, clientRectangle.Height);
        if (this.lastWidth < newRealWidth) {
            this.lastWidth = newRealWidth;
        }
        // сбросим
        if (this.lastLinesCount !== newLinesCount) {
            this.width = 0;
        }
    }
    Draw(gr, window, cashItemSeries, lastMouseMovePt) {
        if (window.IsMainWindow) {
            const backgroundBrush = PlateBase.PlateDefaultBrush;
            const shadowBlur1 = new ShadowBrush(ThemeManager.colors.chartPlateShadowColor, 8, 0, 4);
            const shadowBlur2 = new ShadowBrush(ThemeManager.colors.chartPlateShadowColor, 8, 0, 0);
            gr.drawRoundRectShadow(this.plateRect, 4, shadowBlur1, backgroundBrush.Color);
            gr.drawRoundRectShadow(this.plateRect, 4, shadowBlur2, backgroundBrush.Color);
            gr.RoundRectangle(this.plateRect, 4, null, PlateBase.BorderPlateColorPen);
        }
        gr.save();
        if (window.IsMainWindow) {
            const path = new Path2D();
            const borderW = 1;
            path.roundRect(this.plateRect.X + borderW, this.plateRect.Y + borderW, this.plateRect.Width - borderW * 2, this.plateRect.Height - borderW * 2, 4);
            gr.clip(path);
        }
        this.drawEllipse(gr, lastMouseMovePt);
        this.drawPlatesBack(gr, this.platesBack, window.IsMainWindow);
        const name = this.getTextName();
        gr.DrawStringInRect(name, PlateBase.font, PlateBase.plateForegroundBrush, this.nameRect, false, 'center', 'middle');
        this.drawVisibilityBtn(gr, lastMouseMovePt, window.IsMainWindow);
        this.drawCloseBtn(gr, lastMouseMovePt, window.IsMainWindow);
        gr.restore();
    }
    ProcessMouseMove(X, Y) {
        if (this.closeRectangle.Contains(X, Y)) {
            if (this.lastHover !== PlateHoverEnum.Close) {
                this._ProcessMouseOverInnerElements(this.getCloseTT());
                this.lastHover = PlateHoverEnum.Close;
            }
        }
        else if (this.visibilityRectangle.Contains(X, Y)) {
            if (this.lastHover !== PlateHoverEnum.Visibility) {
                this._ProcessMouseOverInnerElements(this.getVisibilityTT());
                this.lastHover = PlateHoverEnum.Visibility;
            }
        }
        else {
            if (this.lastHover !== PlateHoverEnum.Body) {
                this._ProcessMouseOverInnerElements(this.getTypeName());
                this.lastHover = PlateHoverEnum.Body;
            }
        }
    }
    _ProcessMouseOverInnerElements(text) {
        clearInterval(this.tooltip);
        this.tooltip = null;
        this.tooltip = setTimeout(function (text) {
            if (!ClientType.IsWebMobile()) {
                showTooltip(null, text);
            }
        }, 500, text);
    }
    ProcessMouseLeave() {
        clearInterval(this.tooltip);
        this.tooltip = null;
        this.lastHover = PlateHoverEnum.None;
        if (!ClientType.IsWebMobile()) {
            hideTooltip();
        }
    }
    getTypeName() {
        return '';
    }
    getCloseTT() {
        return '';
    }
    getVisibilityTT() {
        return '';
    }
    getPropertiesText() {
        return '';
    }
    getCloseText() {
        return '';
    }
    getVisibilityText() {
        return '';
    }
    getTextName() { }
    getTextValue(lastMouseHoverBarIndex, lineNumber, seriesSettings) { }
    getBrush(lineNumber) { }
    getLinesCount() { }
    Dispose() { }
    static ThemeChanged() {
        PlateBase.font = ThemeManager.Fonts.Font_12F_regular;
        PlateBase.closeImgDefault = ThemeManager.NewImages.closePlateIconDefault;
        PlateBase.closeImgHovered = ThemeManager.NewImages.closePlateIconHovered;
        PlateBase.visibilityImgDefault = ThemeManager.NewImages.plateVisibleDataDefault;
        PlateBase.visibilityImgHovered = ThemeManager.NewImages.plateVisibleDataHovered;
        PlateBase.nonVisibilityImgDefault = ThemeManager.NewImages.plateInvisibleDataDefault;
        PlateBase.nonVisibilityImgHovered = ThemeManager.NewImages.plateInvisibleDataHovered;
        PlateBase.plateForegroundBrush = new SolidBrush(ThemeManager.colors.chartPlateForeground);
        PlateBase.plateBackForeground = new SolidBrush(ThemeManager.colors.chartPlateBackForeground);
        PlateBase.PlateHoverBrush = new SolidBrush(ThemeManager.colors.chartPlateHover);
        PlateBase.PlateSelectedBrush = new SolidBrush(ThemeManager.colors.chartPlateSelected);
        PlateBase.ButtonsDividerPen = new Pen(ThemeManager.colors.chartDeviderButtons);
        PlateBase.BorderPlateColorPen = new Pen(ThemeManager.colors.chartCommonBorder);
        PlateBase.ButtonsHoverDividerPen = new Pen(ThemeManager.colors.chartDeviderHoverButtons);
        PlateBase.PlateDefaultBrush = new SolidBrush(ThemeManager.colors.chartPlateDefault);
        PlateBase.ButtonsHoverBrush = new SolidBrush(ThemeManager.colors.chartPlateButtonHovered);
    }
    ResetPlateSize() {
        this.lastParametersWidth = 0;
        this.lastWidth = 0;
    }
    getMouseHoverBarIndex(lastMouseMovePt, window, cashItemSeries) {
        if (isNullOrUndefined(cashItemSeries) || lastMouseMovePt.IsEmpty()) {
            return this.nullBarIndex;
        }
        const ticks = Math.floor(window.PointsConverter.GetDataX(lastMouseMovePt.X));
        const index = Math.floor(cashItemSeries.CashItem.FindIntervalByTime(ticks));
        return index;
    }
    drawEllipse(gr, lastMouseMovePt) {
        if (this.ellipseRectangle.IsEmpty()) {
            return;
        }
        const x = this.ellipseRectangle.X;
        const y = this.ellipseRectangle.Y;
        if (this.GetSelected()) {
            gr.FillEllipse(PlateBase.PlateSelectedBrush, x, y, this.circleDiameter, this.circleDiameter);
        }
        else if (this.ellipseRectangle.Contains(lastMouseMovePt.X, lastMouseMovePt.Y) && !this.GetSelected()) {
            gr.DrawEllipse(PlateBase.ButtonsHoverDividerPen, x, y, this.circleDiameter, this.circleDiameter);
        }
        else {
            gr.DrawEllipse(PlateBase.ButtonsDividerPen, x, y, this.circleDiameter, this.circleDiameter);
        }
    }
    drawPlatesBack(gr, platesBack, IsMainWindow) {
        platesBack.forEach((plateBackRect) => {
            gr.FillRectWithRect(this.getBrush(plateBackRect.index), plateBackRect.rect);
            gr.DrawStringInRect(plateBackRect.text, PlateBase.font, PlateBase.plateBackForeground, plateBackRect.rect, false, 'center', 'middle');
        });
    }
    drawVisibilityBtn(gr, lastMouseMovePt, isMainWindow) {
        if (this.visibilityRectangle.IsEmpty()) {
            return;
        }
        let visibilityIm = PlateBase.visibilityImgDefault.image;
        if (!this.GetVisible()) {
            visibilityIm = PlateBase.nonVisibilityImgDefault.image;
        }
        // hovered state
        if (this.visibilityRectangle.Contains(lastMouseMovePt.X, lastMouseMovePt.Y)) {
            if (this.GetVisible()) {
                visibilityIm = PlateBase.visibilityImgHovered.image;
            }
            else {
                visibilityIm = PlateBase.nonVisibilityImgHovered.image;
            }
            if (isMainWindow) {
                gr.RoundRectangle(this.visibilityRectangle, 4, PlateBase.ButtonsHoverBrush);
            }
            else {
                gr.FillRectWithRect(PlateBase.ButtonsHoverBrush, this.visibilityRectangle);
            }
        }
        gr.DrawCenteredImage(visibilityIm, this.visibilityRectangle);
    }
    drawCloseBtn(gr, lastMouseMovePt, isMainWindow) {
        if (this.closeRectangle.IsEmpty()) {
            return;
        }
        let closeIm = PlateBase.closeImgDefault.image;
        if (this.closeRectangle.Contains(lastMouseMovePt.X, lastMouseMovePt.Y)) {
            closeIm = PlateBase.closeImgHovered.image;
            if (isMainWindow) {
                gr.RoundRectangleFilter(this.closeRectangle, 4, PlateBase.ButtonsHoverBrush, null, true, false);
            }
            else {
                gr.FillRectWithRect(PlateBase.ButtonsHoverBrush, this.closeRectangle);
            }
        }
        gr.DrawCenteredImage(closeIm, this.closeRectangle);
    }
}
export var PlateHoverEnum;
(function (PlateHoverEnum) {
    PlateHoverEnum[PlateHoverEnum["None"] = 0] = "None";
    PlateHoverEnum[PlateHoverEnum["Body"] = 1] = "Body";
    PlateHoverEnum[PlateHoverEnum["Close"] = 2] = "Close";
    PlateHoverEnum[PlateHoverEnum["Edit"] = 3] = "Edit";
    PlateHoverEnum[PlateHoverEnum["Visibility"] = 4] = "Visibility";
})(PlateHoverEnum || (PlateHoverEnum = {}));
